import HighCharts from 'highcharts';
import HighChartsReact from 'highcharts-react-official';
import React, { useMemo, useState, useEffect } from 'react';
import { bool, shape, arrayOf, func, object } from 'prop-types';

import { formatDate } from 'utils/';
import { useSelectOption } from 'hooks/';
import { FlexCol } from 'components/Layout/';
import { Loading } from 'components/Loaders/';
import { InfoAlert } from 'components/Alerts/';
import { SelectInput } from 'components/Inputs/';
import { RoundButton } from 'components/Buttons/';
import { black, white, primary } from 'constants/color';
import DatePicker from 'components/DatePicker/DatePicker';
import { ReactComponent as TickIcon } from 'icons/tick.svg';
import {
  StyledContainer,
  StyledGraphWrapper,
  StyledFilterWrapper,
  StyledButtonWrapper,
} from 'components/Item/StockMoveGraph/StockMoveGraph.styled';

const styles = {
  color: primary,
  fontSize: '12px',
  fontWeight: 'normal',
};

/**
 * @param {{
 * items : []
 * warehouses : []
 * stockMove : {
 *  data : [],
 *  isLoading : boolean
 * }
 * onLoadStockMoveGraph : Function
 * }} param
 */
function StockMoveGraph({
  items,
  stockMove,
  warehouses,
  onLoadStockMoveGraph,
}) {
  const today = new Date();
  // 30 days ago
  const monthAgo = new Date().setDate(today.getDate() - 30);
  const [state, setState] = useState({
    warehouse: null,
    inventory: null,
    upperDate: today,
    lowerDate: new Date(monthAgo),
  });
  const { data, isLoading } = stockMove;
  const [errors, setErrors] = useState({
    warehouse: '',
    lowerDate: '',
    upperDate: '',
  });
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [inventoryOption, setInventoryOption] = useState(null);
  const [warehouseOption, setWarehouseOption] = useState(null);
  const warehouseOptions = useSelectOption({ items: warehouses });
  const inventoryOptions = useSelectOption({
    items: warehouseItems,
    label: 'item_name',
  });

  const chartData = useMemo(() => {
    return data.map(function (item) {
      return {
        name: item.name,
        data: item.data.map(function (stock) {
          const date = new Date(stock[0]);
          return [date.getTime(), stock[1]];
        }),
      };
    });
  }, [data]);

  const options = {
    credits: {
      enabled: false,
    },
    yAxis: {
      labels: {
        style: styles,
      },
      title: {
        style: styles,
        text: 'Orders',
      },
    },
    series: chartData,
    title: {
      style: styles,
      text: 'Stock Move',
    },
    plotOptions: {
      series: {
        marker: {
          radius: 2.5,
          lineWidth: 1,
          enabled: true,
          zoomType: 'x',
          lineColor: null,
          symbol: 'circle',
          fillColor: white,
          zooming: {
            mouseWheel: true,
          },
        },
      },
    },
    chart: {
      type: 'spline',
      ignoreHiddenSeries: true,
      backgroundColor: 'transparent',
    },
    legend: {
      enabled: chartData.length < 10,
    },
    xAxis: {
      title: {
        text: 'Date',
        style: styles,
      },
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%e-%b-%y',
      },
      labels: {
        rotation: -45,
        style: styles,
        style: {
          color: primary,
          fontSize: '10px',
        },
        allowOverlap: false,
        format: '{value:%e-%b-%y}',
      },
      // tickPositioner: function () {
      //   return this.series[0].processedXData;
      // },
    },
    tooltip: {
      headerFormat: `<span style='color:${primary};font-size:14px;'>{series.name}</span><br/>`,
      pointFormat: `<p style='color:${black};font-size:12px;'>{point.x:%e-%b-%Y %H:%M} : {point.y:.2f}</p>`,
    },
  };

  /**
   * @param {{
   *  label: string
   *  value: string
   * }} option
   */
  function onWarehouseChange(option) {
    let inventory = [];
    let warehouse = null;

    setWarehouseOption(option);

    if (option) {
      const { value } = option;
      warehouse = value;
      inventory = items.filter((item) => item.warehouse_id === value);
    }

    setState({
      ...state,
      warehouse,
    });
    setWarehouseItems(inventory);
  }

  /**
   * @param {{
   *  label: string
   *  value: string
   * }} option
   */
  function onInventoryChange(option) {
    let inventory = null;

    setInventoryOption(option);

    if (option) {
      inventory = option.value;
    }

    setState({
      ...state,
      inventory,
    });
  }

  /**
   * @param {string} date
   */
  function onLowerDateChange(date) {
    setState({
      ...state,
      lowerDate: date,
    });
  }

  /**
   * @param {string} date
   */
  function onUpperDateChange(date) {
    setState({
      ...state,
      upperDate: date,
    });
  }

  function onFilterGraph() {
    let isValid = true;
    const errorObject = { ...errors };
    const { warehouse, inventory, lowerDate, upperDate } = state;

    if (!warehouse) {
      isValid = false;

      setErrors({
        ...errorObject,
        warehouse: 'Select warehouse to filter graph',
      });
    } else {
      isValid = true;

      setErrors({
        ...errorObject,
        lowerDate: '',
      });
    }
    if (!lowerDate && upperDate) {
      isValid = false;

      setErrors({
        ...errorObject,
        lowerDate: 'Select date from',
      });
    } else {
      setErrors({
        ...errorObject,
        lowerDate: '',
        upperDate: '',
      });
    }
    if (lowerDate && !upperDate) {
      isValid = false;

      setErrors({
        ...errorObject,
        upperDate: 'Select date to',
      });
    } else {
      setErrors({
        ...errorObject,
        lowerDate: '',
        upperDate: '',
      });
    }
    if (lowerDate > upperDate) {
      isValid = false;

      setErrors({
        ...errorObject,
        upperDate: 'Date to is less than date from',
        lowerDate: 'Date from is greater than date to',
      });
    } else {
      setErrors({
        ...errorObject,
        lowerDate: '',
        upperDate: '',
      });
    }

    if (isValid) {
      setErrors({
        warehouse: '',
        lowerDate: '',
        upperDate: '',
      });

      onLoadStockMoveGraph({
        warehouseId: warehouse,
        inventoryId: inventory,
        lowerDate: formatDate({ date: lowerDate, standard: true }),
        upperDate: formatDate({ date: upperDate, standard: true }),
      });
    }
  }

  useEffect(() => {
    if (warehouseOptions.length > 0) {
      onWarehouseChange(warehouseOptions[0]);
      onFilterGraph();
    }
  }, [warehouseOptions]);

  function renderGraph() {
    if (isLoading) {
      return <Loading />;
    } else if (data.length > 0) {
      return (
        <StyledGraphWrapper>
          <HighChartsReact
            options={options}
            highcharts={HighCharts}
            containerProps={{ style: { height: '100%' } }}
          />
        </StyledGraphWrapper>
      );
    } else {
      return <InfoAlert message="No Data To Draw Stock Move Graph" />;
    }
  }

  return (
    <StyledContainer>
      <StyledFilterWrapper>
        <FlexCol flexWidth={3}>
          <SelectInput
            isRequired
            name="warehouse"
            isLoading={false}
            isClearable={true}
            caption="Warehouse"
            isSearchable={true}
            value={warehouseOption}
            ariaLabelledBy="warehouse"
            options={warehouseOptions}
            onChange={onWarehouseChange}
            placeholder="Select warehouse"
            errorMessage={errors.warehouse}
          />
        </FlexCol>
        <FlexCol flexWidth={2}>
          <SelectInput
            name="inventory"
            isLoading={false}
            isClearable={true}
            caption="Inventory"
            isSearchable={true}
            value={inventoryOption}
            options={inventoryOptions}
            ariaLabelledBy="inventory"
            onChange={onInventoryChange}
            placeholder="Select inventory"
          />
        </FlexCol>
        <FlexCol flexWidth={2}>
          <DatePicker
            caption="From"
            name="lowerDate"
            iconTopMargin={30}
            inputId="lowerDate"
            selected={state.lowerDate}
            onChange={onLowerDateChange}
            placeholder="Select date from"
            errorMessage={errors.lowerDate}
          />
        </FlexCol>
        <FlexCol flexWidth={2}>
          <DatePicker
            caption="To"
            name="upperDate"
            iconTopMargin={30}
            inputId="upperDate"
            selected={state.upperDate}
            placeholder="Select date to"
            onChange={onUpperDateChange}
            errorMessage={errors.upperDate}
          />
        </FlexCol>
        <StyledButtonWrapper>
          <RoundButton
            caption="Load"
            flexWidth={100}
            icon={<TickIcon />}
            onClick={onFilterGraph}
          />
        </StyledButtonWrapper>
      </StyledFilterWrapper>
      {renderGraph()}
    </StyledContainer>
  );
}

StockMoveGraph.propTypes = {
  items: arrayOf(object),
  stockMove: shape({
    isLoading: bool,
    data: arrayOf(object),
  }),
  warehouses: arrayOf(object),
  onLoadStockMoveGraph: func.isRequired,
};

export default StockMoveGraph;
